import axios from "axios";
import { addMonths } from "date-fns/addMonths";
import { endOfMonth } from "date-fns/endOfMonth";
import { format } from "date-fns/format";
import { parse } from "date-fns/parse";
import { startOfMonth } from "date-fns/startOfMonth";
import { calendarQuery } from "./calendarQuery.js";
import { canBuildOWCalendarRequest, canBuildRTCalendarRequest } from "./canBuildRTCalendarRequest.js";
import { ONE_WAY, ROUND_TRIP } from "../constants.js";
const validCabinClass = [
    'ECO',
    'PEC',
    'BUS',
    'FST'
];
const ORIGIN = 'origin';
const DESTINATION = 'destination';
function buildOWPricedCalendarRequest({ tripDetails, displayMonths }) {
    if (!canBuildOWCalendarRequest({
        tripDetails,
        displayMonths
    })) return;
    const { displayedFromMonth, displayedToMonth } = displayMonths || {};
    const { flights, cabinClass } = tripDetails;
    const fromDate = getStartOfMonth(displayedFromMonth || '');
    const toDate = getEndOfMonth(displayedToMonth || '');
    const trips = flights.map((flight)=>{
        const originLocation = getLocationCode(flight.departureAirport, ORIGIN);
        const destinationLocation = getLocationCode(flight.arrivalAirport, DESTINATION);
        const trip = {
            ...originLocation,
            ...destinationLocation
        };
        trip.departDateRange = {
            fromDate,
            toDate
        };
        return trip;
    });
    const request = {
        trips,
        size: 720,
        consumer: 'PCLN-HOME'
    };
    if (validCabinClass.includes(cabinClass)) request.cabinClass = cabinClass;
    return request;
}
function getLocationCode(location, type) {
    const locationCode = {};
    if (location.id === location.cityCode) locationCode[`${type}City`] = [
        location.id
    ];
    else locationCode[`${type}`] = [
        location.id
    ];
    return locationCode;
}
function getStartOfMonth(displayedFromMonth) {
    const today = new Date();
    const fromMonth = startOfMonth(parse(displayedFromMonth, 'yyyyMM', today));
    return format(today.valueOf() > fromMonth.valueOf() ? today : fromMonth, 'yyyy-MM-dd');
}
function getEndOfMonth(displayedToMonth) {
    return format(endOfMonth(parse(displayedToMonth, 'yyyyMM', new Date())), 'yyyy-MM-dd');
}
function pricePromise({ request, getGraphURL, apolloClientInfo }) {
    return new Promise((resolve, reject)=>{
        const data = {
            operationName: 'airPriceGuideCalendar',
            variables: {
                input: request
            },
            query: calendarQuery
        };
        const url = getGraphURL?.() || '/pws/v0/pcln-graph/';
        axios({
            url,
            method: 'post',
            data,
            headers: {
                'apollographql-client-name': apolloClientInfo?.clientName,
                'apollographql-client-version': apolloClientInfo?.clientVersion
            }
        }).then((searchResult)=>{
            if (searchResult?.data?.data?.airPriceGuide?.records?.length) resolve(searchResult.data.data.airPriceGuide.records);
            resolve([]);
        }).catch((error)=>{
            reject(error);
        });
    });
}
function buildRTPricedCalendarRequest({ tripDetails, displayMonths, departDate }) {
    if (!canBuildRTCalendarRequest({
        tripDetails,
        displayMonths,
        departDate
    })) return;
    const { displayedFromMonth, displayedToMonth } = displayMonths;
    const { flights, cabinClass } = tripDetails;
    const departureDate = departDate || flights[0].departureDate || '';
    const fromDate = getStartOfMonth(displayedFromMonth);
    const toDate = getEndOfMonth(displayedToMonth);
    const trips = flights.flatMap((flight)=>{
        const departOriginLocation = getLocationCode(flight.departureAirport, ORIGIN);
        const departDestinationLocation = getLocationCode(flight.arrivalAirport, DESTINATION);
        const returnOriginLocation = getLocationCode(flight.arrivalAirport, ORIGIN);
        const returnDestinationLocation = getLocationCode(flight.departureAirport, DESTINATION);
        return [
            {
                ...departOriginLocation,
                ...departDestinationLocation,
                departDate: normalizeDate(departureDate)
            },
            {
                ...returnOriginLocation,
                ...returnDestinationLocation,
                departDateRange: {
                    fromDate,
                    toDate
                }
            }
        ];
    });
    const request = {
        trips,
        size: 720,
        consumer: 'PCLN-HOME'
    };
    if (validCabinClass.includes(cabinClass)) request.cabinClass = cabinClass;
    return request;
}
function normalizeDate(date) {
    if (!date) return '';
    return date.substr(0, 4) + '-' + date.substr(4, 2) + '-' + date.substr(6, 2);
}
function processRecords({ apiRecords, doubleCalendarPricing = false }) {
    let dayMap = {};
    apiRecords.forEach((apiRecord)=>{
        dayMap = apiRecord.reduce((total, record)=>{
            if (record?.dates[0] && record?.minimumFare?.currency === 'USD' && record?.minimumFare?.amtPerPax > 0) {
                const priceDate = record.dates[1] || record.dates[0];
                const dateKey = priceDate.replace(/-/g, '');
                const price = doubleCalendarPricing ? 2 * Math.ceil(record.minimumFare.amtPerPax) : Math.ceil(record.minimumFare.amtPerPax);
                if (!total[dateKey] || total[dateKey].price > price) total[dateKey] = {
                    price
                };
            }
            return total;
        }, dayMap);
    });
    return dayMap;
}
function shouldCallCalendarApi({ flightDetails }) {
    const tripType = flightDetails?.tripType === ONE_WAY || flightDetails?.tripType === ROUND_TRIP;
    const origin = flightDetails?.flights?.[0]?.departureAirport?.id;
    const destination = flightDetails?.flights?.[0]?.arrivalAirport?.id;
    return !!(tripType && origin && destination);
}
const getDisplayMonths = ({ startDate, numberOfMonths = 1 })=>{
    const endDate = addMonths(startDate, numberOfMonths);
    return {
        displayedFromMonth: format(startDate, 'yyyyMM'),
        displayedToMonth: format(endDate, 'yyyyMM')
    };
};
function shouldRequestMonthFromApi({ displayMonths, executedMonths }) {
    const { displayedFromMonth, displayedToMonth } = displayMonths || {};
    if (!displayedToMonth || !displayedFromMonth) return false;
    let fromMonth = parse(displayedFromMonth, 'yyyyMM', new Date());
    const toMonth = parse(displayedToMonth, 'yyyyMM', new Date());
    while(fromMonth.valueOf() <= toMonth.valueOf()){
        const fromMonthKey = format(fromMonth, 'yyyyMM');
        if (executedMonths.current && !executedMonths.current[fromMonthKey]) return true;
        fromMonth = addMonths(fromMonth, 1);
    }
    return false;
}
export { buildOWPricedCalendarRequest, buildRTPricedCalendarRequest, getDisplayMonths, getEndOfMonth, getLocationCode, getStartOfMonth, normalizeDate, pricePromise, processRecords, shouldCallCalendarApi, shouldRequestMonthFromApi };
